import {
  ConfigurationKey,
  COVERS_CONFIGURATION_KEYS,
  DECORATIVE_CONFIGURATION_KEYS,
  FITTING_CONFIGURATION_KEYS,
  GEOMETRY_CONFIGURATION_KEYS,
  SystemClass,
  SystemThickness,
} from '@cutr/constants/windows/windows-configurations.ts';
import { useFlag } from '@unleash/proxy-client-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/blocks/Modal';
import { Button } from '@/primitives/Button';
import { Checkbox } from '@/primitives/Checkbox';
import { Icon, SlidersHorizontal } from '@/primitives/Icons';
import { useToggle } from '@/utils/hooks';
import { useWindowStore } from '@/windows/api/windowStore';
import { SORPETALER_IMAGES } from '@/windows/data/sorpetaler-images';

import { Carousel } from '../../blocks/Carousel';
import { OrderLevelConfigurations, unboxLabel } from './Configurations';
import { useConfigurations } from './ConfigurationsContext';
import styles from './OrderConfiguration.module.css';

export const OrderConfiguration = ({
  readonly = false,
}: {
  readonly?: boolean;
}) => {
  return (
    <section
      className="orderConfigurationLayout"
      style={{
        backgroundColor: 'var(--gray-1)',
        borderRadius: 'var(--radius-s)',
      }}
    >
      <SystemPreview />
      <div className={styles.column}>
        <OrderLevelConfigurations renderMode={readonly ? 'readonly' : 'form'} />
        <CustomizeOrderConfiguration />
      </div>
    </section>
  );
};

const CustomizeOrderConfiguration = () => {
  const { t } = useTranslation();
  const [isOpen, toggleModal] = useToggle(false);

  return (
    <>
      <Button
        variant="secondary"
        className="full"
        onClick={toggleModal}
        icon={<Icon icon={<SlidersHorizontal />} />}
      >
        {t('reseller.customizeOrderConfiguration.button.label')}
      </Button>
      {isOpen && (
        <Modal
          title={t('reseller.customizeOrderConfiguration.modal.title')}
          isOpen={isOpen}
          handleClose={toggleModal}
        >
          <CustomizeOrderConfigurationModal toggleModal={toggleModal} />
        </Modal>
      )}
    </>
  );
};

const CustomizeOrderConfigurationModal = ({
  toggleModal,
}: {
  toggleModal: () => void;
}) => {
  const { t } = useTranslation();
  const { configurations: orderConfigurations } = useWindowStore();
  const { dispatch } = useConfigurations();
  const isBeamWidthEnabled = useFlag('custom-beam-widths');
  const coversKeys = COVERS_CONFIGURATION_KEYS;
  const geometryKeys = GEOMETRY_CONFIGURATION_KEYS.filter(
    (key) =>
      (key !== 'frameBeamWidth' && key !== 'sashBeamWidth') ||
      isBeamWidthEnabled
  );
  const fittingKeys = FITTING_CONFIGURATION_KEYS.filter(
    (key) => key === 'coverStrips' || key === 'plasteringStrips'
  );
  const decorativeKeys = DECORATIVE_CONFIGURATION_KEYS.filter(
    (key) => key === 'sashbars'
  );
  const [enabledConfigurationKeys, setEnabledConfigurationKeys] =
    React.useState<ConfigurationKey[]>(
      Object.keys(orderConfigurations) as ConfigurationKey[]
    );

  const saveConfigurations = () => {
    const newOrderConfigurations = Object.fromEntries(
      enabledConfigurationKeys.map((key) => [key, orderConfigurations[key]])
    );

    const oldConfigurationKeys = Object.keys(
      orderConfigurations
    ) as ConfigurationKey[];
    const newConfigurationKeys = Object.keys(
      newOrderConfigurations
    ) as ConfigurationKey[];

    const toDelete = oldConfigurationKeys.filter(
      (key) => !newConfigurationKeys.includes(key)
    );

    const toAdd = newConfigurationKeys.filter(
      (key) => !oldConfigurationKeys.includes(key)
    );

    dispatch({
      type: 'CUSTOMIZE_CONFIGURATIONS',
      toDelete: toDelete,
      toAdd: toAdd,
    });

    toggleModal();
  };

  return (
    <div className={styles.customizeOrderConfigurationModal}>
      <p>{t('reseller.customizeOrderConfiguration.modal.description')}</p>
      <div className={styles.configurationContainer}>
        <h3>
          {t(
            'reseller.customizeOrderConfiguration.modal.geometryConfigurations'
          )}
        </h3>
        {geometryKeys.map((key) => {
          return (
            <React.Fragment key={key}>
              <Checkbox
                checked={enabledConfigurationKeys.includes(key)}
                onChange={() =>
                  enabledConfigurationKeys.includes(key)
                    ? setEnabledConfigurationKeys(
                        enabledConfigurationKeys.filter((k) => k !== key)
                      )
                    : setEnabledConfigurationKeys([
                        ...enabledConfigurationKeys,
                        key,
                      ])
                }
              >
                {t(`windows.configurations.${key}.label`)}
              </Checkbox>
            </React.Fragment>
          );
        })}
        <h3>
          {t('reseller.customizeOrderConfiguration.modal.coversConfigurations')}
        </h3>
        {coversKeys.map((key) => {
          return (
            <React.Fragment key={key}>
              <Checkbox
                checked={enabledConfigurationKeys.includes(key)}
                onChange={() =>
                  enabledConfigurationKeys.includes(key)
                    ? setEnabledConfigurationKeys(
                        enabledConfigurationKeys.filter((k) => k !== key)
                      )
                    : setEnabledConfigurationKeys([
                        ...enabledConfigurationKeys,
                        key,
                      ])
                }
              >
                {t(`windows.configurations.${key}.label`)}
              </Checkbox>
            </React.Fragment>
          );
        })}
        {fittingKeys.length > 0 && (
          <>
            <h3>
              {t(
                'reseller.customizeOrderConfiguration.modal.fittingConfigurations'
              )}
            </h3>
            {fittingKeys.map((key) => {
              return (
                <React.Fragment key={key}>
                  <Checkbox
                    checked={enabledConfigurationKeys.includes(key)}
                    onChange={() =>
                      enabledConfigurationKeys.includes(key)
                        ? setEnabledConfigurationKeys(
                            enabledConfigurationKeys.filter((k) => k !== key)
                          )
                        : setEnabledConfigurationKeys([
                            ...enabledConfigurationKeys,
                            key,
                          ])
                    }
                  >
                    {t(`windows.configurations.${key}.label`)}
                  </Checkbox>
                </React.Fragment>
              );
            })}
          </>
        )}
        {decorativeKeys.length > 0 && (
          <>
            <h3>
              {t(
                'reseller.customizeOrderConfiguration.modal.decorativeConfigurations'
              )}
            </h3>
            {decorativeKeys.map((key) => {
              return (
                <React.Fragment key={key}>
                  <Checkbox
                    checked={enabledConfigurationKeys.includes(key)}
                    onChange={() =>
                      enabledConfigurationKeys.includes(key)
                        ? setEnabledConfigurationKeys(
                            enabledConfigurationKeys.filter((k) => k !== key)
                          )
                        : setEnabledConfigurationKeys([
                            ...enabledConfigurationKeys,
                            key,
                          ])
                    }
                  >
                    {t(`windows.configurations.${key}.label`)}
                  </Checkbox>
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>
      <Button onClick={saveConfigurations} className={styles.buttonContainer}>
        {t('reseller.customizeOrderConfiguration.modal.save')}
      </Button>
    </div>
  );
};

export const SystemPreview = () => {
  const { t } = useTranslation();

  const { configurations } = useConfigurations();
  const thickness = configurations.thickness?.value as SystemThickness;
  const systemClass = configurations.system?.value as SystemClass;
  const items = Object.entries(
    // @ts-expect-error: typing is hard
    SORPETALER_IMAGES[systemClass]?.[thickness] || {}
  ).map(([imageName, image]) => ({
    id: imageName,
    // @ts-expect-error: typing is hard
    imagePath: image.imagePath,
  }));

  return (
    <div className={styles.column}>
      <h3>
        {t('reseller.common.preview', {
          system: unboxLabel(configurations.system?.label),
          thickness,
        })}
      </h3>
      <Carousel
        items={items}
        key={`${systemClass}-${thickness}`}
        width={512}
        height={512}
      />
    </div>
  );
};
